import { IfpApplicationRequestData, IfpLoan, PrimaryReasonForIfp } from '@shared/sec-loans'
import { OpenModalEvent } from '@ui-common/components/Modal'
import { YesNo } from '@ui-common/types'
import { formDateStringToBffDateString, toNumber } from '@ui-common/utils'
import { ModalVisualMode } from '@ui-components/containers/ModalTypes'
import { BodyText } from '@ui-components/typography/Typography'
import { IntlShape } from 'react-intl'

import { isFormDataValid } from '../../../wizard/wizardData'
import { IfpLoansAnalyticsService } from '../../analytics/service'
import { getWizardFlow } from '../../utils'
import { IfpWizardData } from '../../wizard/types'
import { IfpWizardType } from '../../wizard/wizardMachine'
import { createFormValidator } from './formValidator'
import { IfpBasicInfoFormData } from './types'

export const getIfpInfoStepLocalizedEnums = (formatMessage: IntlShape['formatMessage']) => {
  const yesNoTexts: Record<YesNo, string> = {
    [YesNo.Yes]: formatMessage({ id: 'common-yes' }),
    [YesNo.No]: formatMessage({ id: 'common-no' }),
  }

  const primaryReasonLabels: Record<PrimaryReasonForIfp, string> = {
    [PrimaryReasonForIfp.HomeRelocation]: formatMessage({ id: 'ifp-loans-main-reason-home-relocation' }),
    [PrimaryReasonForIfp.LargerPurchases]: formatMessage({ id: 'ifp-loans-primary-reason-larger-purchases' }),
    [PrimaryReasonForIfp.PreparingForSaving]: formatMessage({ id: 'ifp-loans-primary-reason-preparing-for-saving' }),
    [PrimaryReasonForIfp.DivorceOrSeparation]: formatMessage({ id: 'ifp-loans-primary-reason-divorce-or-separation' }),
    [PrimaryReasonForIfp.Retirement]: formatMessage({ id: 'ifp-loans-primary-reason-retirement' }),
    [PrimaryReasonForIfp.Furlough]: formatMessage({ id: 'ifp-loans-primary-reason-furlough' }),
    [PrimaryReasonForIfp.LongtermIllness]: formatMessage({ id: 'ifp-loans-primary-reason-longterm-illness' }),
    [PrimaryReasonForIfp.Unemployment]: formatMessage({ id: 'ifp-loans-primary-reason-unemployment' }),
    [PrimaryReasonForIfp.AllocateFundsForOtherCredits]: formatMessage({
      id: 'ifp-loans-primary-reason-allocate-funds-for-other-credits',
    }),
    [PrimaryReasonForIfp.CompanysWeakFinancialSituation]: formatMessage({
      id: 'ifp-loans-primary-reason-companys-weak-financial-situation',
    }),
    [PrimaryReasonForIfp.ParentalLeave]: formatMessage({ id: 'ifp-loans-primary-reason-parental-leave' }),
    [PrimaryReasonForIfp.StudyLeave]: formatMessage({ id: 'ifp-loans-primary-reason-study-leave' }),
    [PrimaryReasonForIfp.Other]: formatMessage({ id: 'ifp-loans-primary-reason-other' }),
  }

  return {
    yesNoTexts,
    primaryReasonLabels,
  }
}

export const getHasDependantChanges = ({
  loan,
  formData,
  wizardType,
}: {
  loan: IfpLoan
  formData: IfpBasicInfoFormData
  wizardType: IfpWizardType | undefined
}): boolean => {
  const newWizardType = getWizardFlow({
    durationInMonths: toNumber(formData.durationInMonths),
    principalExemptionMonthsLeft: loan.principalExemptionMonthsLeft,
    hasFinancialDifficulties: formData.hasFinancialDifficulties,
  })
  return wizardType !== newWizardType
}

export const showDependantChangesModal = (
  modalSend: (openModalEvent: OpenModalEvent) => void,
  formatMessage: IntlShape['formatMessage'],
  analyticsService: IfpLoansAnalyticsService,
  onSubmit: () => void,
): void => {
  const modalAnalyticsName = 'ConfirmDataDependencyUpdateModal'
  const openEvent: OpenModalEvent = {
    type: 'OPEN',
    data: {
      header: formatMessage({ id: 'sec-loans-dependant-changes-modal-header' }),
      children: <BodyText tagName="div">{formatMessage({ id: 'sec-loans-dependant-changes-modal-message' })}</BodyText>,
      primaryButtonOptions: {
        label: formatMessage({ id: 'common-cancel' }),
        type: 'primary',
        onClick: () => analyticsService.sendCustomEvent(modalAnalyticsName, 'CancelButton'),
      },
      secondaryButtonOptions: {
        label: formatMessage({ id: 'common-continue-anyway' }),
        onClick: () => {
          analyticsService.sendCustomEvent(modalAnalyticsName, 'ConfirmButton')
          onSubmit()
        },
      },
      mode: ModalVisualMode.ThinkTwice,
    },
  }
  analyticsService.sendModalViewEvent(modalAnalyticsName)
  modalSend(openEvent)
}

export const isIfpInfoValid = (data: IfpBasicInfoFormData | undefined): boolean => {
  return !!data && isFormDataValid(data, createFormValidator)
}

export const getAndValidateIfpInfoData = (
  data: Partial<IfpWizardData>,
): IfpApplicationRequestData['basicInfo'] | undefined => {
  const basicInfo = data.basicInfo
  const isValid = basicInfo && isIfpInfoValid(basicInfo)
  return isValid
    ? {
        durationInMonths: parseInt(basicInfo.durationInMonths),
        startsImmediately: basicInfo.startsImmediately === YesNo.Yes,
        startDate: formDateStringToBffDateString(basicInfo.startDate),
        primaryReason: basicInfo.primaryReason,
        primaryReasonOther: basicInfo.primaryReasonOther,
        hasFinancialDifficulties: basicInfo.hasFinancialDifficulties === YesNo.Yes,
      }
    : undefined
}
